// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Shipment } from '@/shipment/api/types';

// Measured per-unit resync cost (asset create + audit + inherent overhead). Asset creations
// (POs) and AEG updates (ROs/FOs) are treated as equal cost, so one figure serves all three.
const MS_PER_RESYNC_UNIT = 100;

// Manual resync is disabled above this estimate; such orders are left to the automatic sweeps.
export const MAX_MANUAL_RESYNC_SECONDS = 10 * 60;

/**
 * Counts the SKU quantities of asset-backed line items across all of an order's shipments —
 * a resync reprocesses the whole order, moving each of these units as an asset create (POs)
 * or AEG change (ROs/FOs) regardless of the shipment's current status.
 */
export function countResyncImpactedUnits(shipments: Shipment[]): number {
  return shipments
    .flatMap((shipment) => shipment.items)
    .reduce((total, shipmentItem) => {
      if (shipmentItem.item.hasAssets) {
        return total + (shipmentItem.quantity ?? 0);
      }
      return total;
    }, 0);
}

/**
 * Estimates resync duration in seconds (~100ms per asset/AEG unit), rounded up.
 */
export function estimateResyncSeconds(shipments: Shipment[]): number {
  const units = countResyncImpactedUnits(shipments);
  if (units <= 0) {
    return 0;
  }
  return Math.ceil((units * MS_PER_RESYNC_UNIT) / 1000);
}
